// Типографика. Источник: design/README.md, таблица ролей и раздел «Веб-версия».
//
// Две гарнитуры и никакой третьей. Alegreya Sans — только заголовки и
// логотип, в теле не используется. Файлы привезены в ../fonts статикой,
// с CDN ничего не тянется; там же сказано, почему React Native придётся
// привозить их заново.

/** Файлы лежат в ../fonts, подключаются через ../fonts/fonts.css. */
export const FONTS = {
  display: 'Alegreya Sans',
  ui: 'Manrope',
  mono: 'ui-monospace',
} as const

export const FONT_WEIGHTS = {
  display: [500, 700],
  ui: [400, 500, 600, 700],
} as const

export interface TextStyle {
  font: (typeof FONTS)[keyof typeof FONTS]
  size: number
  /** Множитель к размеру. */
  lineHeight: number
  weight: number
  /** В em, как в дизайне. */
  tracking?: number
  uppercase?: boolean
}

export const TEXT = {
  h1: { font: FONTS.display, size: 38, lineHeight: 1.08, weight: 700, tracking: -0.015 },
  h2: { font: FONTS.display, size: 28, lineHeight: 1.12, weight: 700, tracking: -0.015 },
  sheetTitle: { font: FONTS.display, size: 26, lineHeight: 1.15, weight: 700, tracking: -0.01 },
  logo: { font: FONTS.display, size: 24, lineHeight: 1, weight: 500, tracking: -0.015 },

  /**
   * Обещание на экране входа. В вебе оно крупнее мобильного (52 против 38):
   * колонка шире, и заголовок в 38 потерялся бы в ней.
   */
  hero: { font: FONTS.display, size: 52, lineHeight: 1.06, weight: 700, tracking: -0.02 },
  /** Приветствие на пустом экране кабинета, в две строки. Только веб. */
  greeting: { font: FONTS.display, size: 34, lineHeight: 1.12, weight: 700, tracking: -0.015 },
  /** Слово «смыслика» в шапке кабинета — мельче, чем на экране входа. */
  logoCompact: { font: FONTS.display, size: 21, lineHeight: 1, weight: 500, tracking: -0.015 },

  navTitle: { font: FONTS.ui, size: 16, lineHeight: 1, weight: 600 },
  cardTitle: { font: FONTS.ui, size: 16, lineHeight: 1.2, weight: 600 },
  body: { font: FONTS.ui, size: 15, lineHeight: 1.48, weight: 400 },
  /** Абзац под обещанием входа — крупнее тела. */
  lead: { font: FONTS.ui, size: 19, lineHeight: 1.5, weight: 400 },
  /** Строка-преимущество с точкой. Экран 01. */
  bullet: { font: FONTS.ui, size: 16, lineHeight: 1.3, weight: 500 },
  /** Значение в поле ввода и подпись над ним. */
  fieldValue: { font: FONTS.ui, size: 19, lineHeight: 1, weight: 500 },
  fieldLabel: { font: FONTS.ui, size: 13, lineHeight: 1, weight: 500 },
  buttonPrimary: { font: FONTS.ui, size: 17, lineHeight: 1, weight: 600 },
  buttonSecondary: { font: FONTS.ui, size: 15, lineHeight: 1, weight: 600 },
  chip: { font: FONTS.ui, size: 14, lineHeight: 1, weight: 500 },
  meta: { font: FONTS.ui, size: 13, lineHeight: 1.4, weight: 400 },
  balance: { font: FONTS.ui, size: 17, lineHeight: 1, weight: 700 },
  /** Остаток в шапке кабинета — число над полосой. Обе платформы. */
  balanceCompact: { font: FONTS.ui, size: 14, lineHeight: 1, weight: 700 },
  balanceCaption: { font: FONTS.ui, size: 12, lineHeight: 1, weight: 400 },
  /** Название источника в правой колонке — плотнее, чем мета рядом с ним. */
  sourceName: { font: FONTS.ui, size: 13, lineHeight: 1.35, weight: 500 },
  caps: { font: FONTS.ui, size: 11, lineHeight: 1, weight: 600, tracking: 0.08, uppercase: true },
} as const satisfies Record<string, TextStyle>

export type TextRole = keyof typeof TEXT
